import type { Address } from "@schemas/misc";
import type { EIP2098Signature, VRS } from "@schemas/sign";
import type { Authorization } from "viem";

import { isAddressEqual, toHex, toRlp, zeroAddress } from "viem";

export const generateNonce = (length = 8) => {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const nonce = Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length)),
  ).join("");
  return nonce;
};

const ensureHexPrefix = (value: string): `0x${string}` =>
  value.startsWith("0x") ? (value as `0x${string}`) : `0x${value}`;

export const normalizeYParityValue = (
  value: number | bigint | string | undefined | null,
): 0 | 1 => {
  if (value === undefined || value === null) return 0;

  let numericValue: number;
  if (typeof value === "number") {
    numericValue = value;
  } else if (typeof value === "bigint") {
    numericValue = Number(value);
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    const parsed = trimmed.startsWith("0x")
      ? parseInt(trimmed, 16)
      : parseInt(trimmed, 10);
    if (Number.isNaN(parsed)) throw new Error("invalid yParity value");
    numericValue = parsed;
  } else {
    throw new Error("invalid yParity type");
  }

  return (Math.abs(numericValue) % 2 === 0 ? 0 : 1) as 0 | 1;
};

export const parseSignatureToVRS = (signature: string): VRS => {
  if (typeof signature !== "string") {
    throw new Error("Signature must be a string");
  }

  if (!signature.startsWith("0x")) {
    throw new Error('Signature must start with "0x"');
  }

  if (signature.length !== 132) {
    throw new Error(
      'Signature must be 65 bytes (130 hex chars) plus "0x" = 132 total length',
    );
  }

  const r = signature.slice(2, 66);
  const s = signature.slice(66, 130);
  const vHex = signature.slice(130, 132);
  const v = parseInt(vHex, 16);
  return { v, r: `0x${r}`, s: `0x${s}` };
};

// overload for accepting authorization object directly
export function createRlpEncodedAuthorization(
  authorization: Authorization,
): `0x${string}`;
// overload for accepting individual parameters
export function createRlpEncodedAuthorization(
  chainId: number,
  delegateAddress: string,
  nonce: number,
  yParity: number | bigint,
  r: `0x${string}`,
  s: `0x${string}`,
): `0x${string}`;
export function createRlpEncodedAuthorization(
  authorizationOrChainId: Authorization | number,
  delegateAddress?: string,
  nonce?: number,
  yParity?: number | bigint,
  r?: `0x${string}`,
  s?: `0x${string}`,
) {
  if (typeof authorizationOrChainId === "object") {
    // using authorization object
    const authorization = authorizationOrChainId;
    const normalizedYParity = normalizeYParityValue(
      authorization.yParity as unknown as number | bigint | string | undefined,
    );
    const rlpInput = [
      toHex(authorization.chainId),
      ensureHexPrefix(authorization.address),
      authorization.nonce === 0 ? "0x" : toHex(authorization.nonce),
      normalizedYParity === 0 ? "0x" : toHex(normalizedYParity),
      authorization.r!,
      authorization.s!,
    ] as const;

    return toRlp(rlpInput);
  } else {
    // using individual parameters
    const chainId = authorizationOrChainId;
    const normalizedYParity = normalizeYParityValue(yParity);
    const rlpInput = [
      toHex(chainId),
      ensureHexPrefix(delegateAddress!),
      nonce === 0 ? "0x" : toHex(nonce!),
      normalizedYParity === 0 ? "0x" : toHex(normalizedYParity),
      r!,
      s!,
    ] as const;

    return toRlp(rlpInput);
  }
}

export const createEIP2098Signature = (vrs: VRS): EIP2098Signature => {
  const v = vrs.v;
  const r = vrs.r;
  const s = vrs.s;

  return { yParity: normalizeV(v), r, s };
};

export const isValidV = (v: number): boolean => {
  return v === 0 || v === 1 || v === 27 || v === 28 || v >= 35;
};

// normalizes v value for eip-155 compliance
export const normalizeV = (v: number) => {
  if (!isValidV(v)) return 0;

  const cmp = v <= 1 ? 1 : 0;
  const result = v % 2 === cmp;

  return Number(result) % 2;
};

export const isNullAddress = (address: Address) => {
  return isAddressEqual(address, zeroAddress);
};
